<template>
    <div>
        <SavePaperGroupDialog
            :paper-group="newPaperGroup"
            :is-open="savePaperGroupDialog"
            @on-save="getPaperGroups"
            @on-close="closeSavePaperGroupDialog"
        />
        <v-dialog
            :value="isOpen"
            max-width="500px"
            transition="slide-y-transition"
            eager
            @keydown.esc="onClose"
            @click:outside="onClose"
        >
            <v-card>
                <v-card-title>
                    <h1 class="text-subtitle-1">
                        <template v-if="!isNewPaper">
                            Edycja papieru <strong>{{ paper.name }}</strong>
                        </template>
                        <template v-else>
                            Tworzenie papieru
                        </template>
                    </h1>
                </v-card-title>

                <v-card-text class="text-center">
                    <v-text-field
                        v-model="paperName"
                        label="Nazwa"
                        outlined
                        dense
                        :error-messages="paperNameErrors"
                        required
                    />

                    <v-select
                        v-model="paperGroupId"
                        :items="paperGroups"
                        item-value="id"
                        label="Grupa"
                        outlined
                        dense
                        :error-messages="paperGroupErrors"
                        required
                    >
                        <template slot="selection" slot-scope="data">
                            {{ data.item.name }}
                        </template>

                        <template slot="item" slot-scope="data">
                            {{ data.item.name }}
                            <v-chip
                                v-if="!data.item.isVisible"
                                color="red"
                                text-color="red"
                                small
                                outlined
                            >
                                nieaktywna
                            </v-chip>
                        </template>
                        <template slot="append-outer">
                            <v-tooltip :open-delay="tooltipDelay" top>
                                <template #activator="{on}">
                                    <v-btn
                                        icon
                                        data-sentry-label="Dodaj grupę"
                                        v-on="on"
                                        @click="showCreatePaperGroupDialog"
                                    >
                                        <v-icon color="primary">
                                            mdi-plus
                                        </v-icon>
                                    </v-btn>
                                </template>
                                <span>Dodaj grupę</span>
                            </v-tooltip>
                        </template>
                    </v-select>

                    <v-select
                        v-model="coatingTypeId"
                        :items="coatingTypes"
                        item-value="id"
                        item-text="name"
                        label="Rodzaj powleczenia"
                        outlined
                        dense
                        :error-messages="coatingTypeErrors"
                        required
                    />

                    <v-select
                        v-model="prinergyPaperTypeId"
                        :items="extendedPrinergyPaperTypes"
                        no-data-text="No options available"
                        item-value="id"
                        item-text="name"
                        label="Typ prinergy"
                        outlined
                        dense
                    />

                    <v-text-field
                        v-model="paperWeight"
                        label="Gramatura"
                        outlined
                        dense
                        :error-messages="paperWeightErrors"
                        required
                        suffix="(g)"
                    />

                    <v-text-field
                        v-model="thickness"
                        label="Grubość"
                        outlined
                        dense
                        :error-messages="paperThicknessErrors"
                        required
                        suffix="(mm)"
                    />

                    <v-text-field
                        v-model="thicknessInPoints"
                        label="Grubość"
                        outlined
                        dense
                        disabled
                        suffix="(punkty)"
                    />

                    <v-text-field
                        v-model="piecePrice"
                        label="Cena za sztukę"
                        outlined
                        dense
                        :disabled="!canChangePrice"
                        :error-messages="piecePriceErrors"
                    />

                    <v-checkbox
                        v-model="isPaperVisible"
                        label="Czy aktywny?"
                    />
                </v-card-text>

                <v-divider />

                <v-card-actions>
                    <v-btn
                        color="error"
                        text
                        data-sentry-label="Anuluj"
                        :disabled="actionLoading"
                        @click="onClose()"
                    >
                        Anuluj
                    </v-btn>
                    <v-spacer />
                    <v-btn
                        color="success"
                        text
                        data-sentry-label="Zapisz"
                        :loading="actionLoading"
                        @click="savePaper"
                    >
                        Zapisz
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { required, numeric } from 'vuelidate/lib/validators'
import { isEmpty } from 'lodash'
import priceFormatter from '@/mixins/priceFormatter'
import distanceFormatter from '@/mixins/distanceFormatter'
import tooltipDelay from '@/mixins/tooltip'
import { errorHandler } from '@/utils/errorHandler'
import { hasUserPermission } from '@/utils/permissionsHandler'
import SavePaperGroupDialog from '@/views/Configurations/Materials/PaperGroups/SavePaperGroupDialog'
import { twoDigitsAfterDecimalWithComma, isGreaterThanZero } from '@/utils/customValidators'

export default {
    name: 'SavePaperDialog',
    components: {
        SavePaperGroupDialog
    },
    mixins: [
        tooltipDelay, priceFormatter, distanceFormatter
    ],
    props: {
        isOpen: {
            type: Boolean
        },
        paper: {
            type: Object,
            default: null
        },
        paperGroups: {
            type: Array,
            default: null
        },
        coatingTypes: {
            type: Array,
            default: null
        },
        prinergyPaperTypes: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            paperName: null,
            paperGroupId: null,
            coatingTypeId: null,
            paperWeight: null,
            piecePrice: null,
            thickness: null,
            thicknessInPoints: null,
            prinergyPaperTypeId: null,
            isPaperVisible: false,
            actionLoading: false,
            newPaperGroup: {},
            savePaperGroupDialog: false
        }
    },
    validations: {
        paperName: {
            required
        },
        paperGroupId: {
            required
        },
        coatingTypeId: {
            required
        },
        paperWeight: {
            required,
            numeric
        },
        piecePrice: {
            twoDigitsAfterDecimalWithComma
        },
        thickness: {
            required,
            twoDigitsAfterDecimalWithComma,
            isGreaterThanZero
        }
    },
    computed: {
        extendedPrinergyPaperTypes() {
            let arr = this.prinergyPaperTypes

            arr.unshift({'id': 0, 'name': 'brak'})

            return arr
        },
        isNewPaper() {
            return isEmpty(this.paper)
        },
        paperGroup() {
            return this.paperGroups.find(group => group.id === this.paperGroupId)
        },
        canChangePrice() {
            return hasUserPermission('papers_price_update') || this.isNewPaper
        },
        // Walidacje
        paperNameErrors() {
            const errors = []

            if (!this.$v.paperName.$dirty) return errors
            !this.$v.paperName.required && errors.push('Proszę podać nazwę papieru.')

            return errors
        },
        paperGroupErrors() {
            const errors = []

            if (!this.$v.paperGroupId.$dirty) return errors
            !this.$v.paperGroupId.required && errors.push('Proszę wybrać grupę papieru.')

            return errors
        },
        coatingTypeErrors() {
            const errors = []

            if (!this.$v.coatingTypeId.$dirty) return errors
            !this.$v.coatingTypeId.required && errors.push('Proszę wybrać rodzaj powleczenia.')

            return errors
        },
        paperWeightErrors() {
            const errors = []

            if (!this.$v.paperWeight.$dirty) return errors
            !this.$v.paperWeight.required && errors.push('Proszę podać gramaturę papieru.')
            !this.$v.paperWeight.numeric && errors.push('Niepoprawny format.')

            return errors
        },
        paperThicknessErrors() {
            const errors = []

            if (!this.$v.thickness.$dirty) return errors
            if (!this.$v.thickness.required || !this.$v.thickness.isGreaterThanZero) {
                errors.push('Proszę podać grubość papieru większą ni 0,00.')
            }
            !this.$v.thickness.twoDigitsAfterDecimalWithComma && errors.push('Niepoprawny format.')

            return errors
        },
        piecePriceErrors() {
            const errors = []

            if (!this.$v.piecePrice.$dirty) return errors
            !this.$v.piecePrice.twoDigitsAfterDecimalWithComma && errors.push('Proszę podać poprawną wartość wartość.')

            return errors
        },
    },
    watch: {
        paper(value) {
            if (!isEmpty(value)) {
                this.paperName = value.name
                this.paperGroupId = value.groupId
                this.coatingTypeId = value.coatingTypeId
                this.prinergyPaperTypeId = value.prinergyPaperTypeId === null ? 0 : value.prinergyPaperTypeId
                this.paperWeight = value.weight
                this.isPaperVisible = value.isVisible
                this.piecePrice = this.formatPrice(value.piecePrice)
                this.thickness = this.formatDistance(value.thickness)
                this.thicknessInPoints = this.formatDistance(value.thicknessInPoints)
            }
        }
    },
    methods: {
        getPaperGroups(payload) {
            this.$store.dispatch('configurations/getPaperGroups', {
                paginate: '',
                query: ''
            })
                .then(() => {
                    this.paperGroupId = payload.id
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        showCreatePaperGroupDialog() {
            this.savePaperGroupDialog = true
        },
        closeSavePaperGroupDialog() {
            this.savePaperGroupDialog = false
        },
        savePaper() {
            this.$v.$touch()

            if (this.$v.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })

                return
            }

            let action = null

            const payload = {
                group: this.paperGroup,
                groupId: this.paperGroupId,
                coatingTypeId: this.coatingTypeId,
                prinergyPaperTypeId: this.prinergyPaperTypeId === 0 ? null : this.prinergyPaperTypeId,
                name: this.paperName,
                weight: this.paperWeight,
                isVisible: this.isPaperVisible,
                thickness: this.unformatDistance(this.thickness)
            }

            if (this.canChangePrice) {
                payload.piecePrice = this.piecePrice === '' ? 0 : this.unformatPrice(this.piecePrice)
            }

            if (this.isNewPaper) {
                action = 'createPaper'
            } else {
                action = 'updatePaper'
                payload.id = this.paper.id
            }

            this.actionLoading = true
            this.$store.dispatch(`configurations/${action}`, payload)
                .then(() => {
                    this.$emit('on-save')
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading = false
                    this.onClose()
                })
        },
        onClose() {
            setTimeout(() => {
                this.paperName = null
                this.paperGroupId = null
                this.coatingTypeId = null
                this.prinergyPaperTypeId = null
                this.paperWeight = null
                this.piecePrice = null
                this.isPaperVisible = false
                this.thickness = null
                this.$v.$reset()
            }, 500)

            this.$emit('on-close')
        }
    }
}
</script>

